import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Wallet, Link2 } from "lucide-react";

const SYM = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

/**
 * Founder-only queue of every `unverified` partner_payment across every
 * account. Bulk-verify (or bulk-reject) with one click so the founder
 * doesn't have to drill into each partner one at a time.
 */
export default function PendingPartnerPaymentsCard() {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/partner-payments/pending");
      setRows(data || []);
      setSel(new Set());
    } catch { /* founder-only endpoint — ignore for non-founders */ }
    finally { setLoaded(true); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel(sel.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));

  const runBulk = async (status) => {
    const ids = [...sel];
    if (ids.length === 0) { toast.error("Pick at least one row"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/partner-payments/bulk-verify", { ids, status });
      toast.success(`${status === "verified" ? "Verified" : "Rejected"} ${data.updated} payment${data.updated === 1 ? "" : "s"}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  // Founder can attribute an unallocated payment to a specific booking
  // at verify-time. Only runs against ONE selected row (booking picker
  // per row would explode the table); nudges Founder if they try with a
  // multi-selection.
  const [attributing, setAttributing] = useState(null); // {payment_id, partner_id}
  const [bookings, setBookings] = useState([]);
  const [bookingSel, setBookingSel] = useState("");
  const openAttribute = async () => {
    if (sel.size !== 1) { toast.error("Pick exactly one payment to attribute"); return; }
    const pid = [...sel][0];
    const row = rows.find(r => r.id === pid);
    if (!row) return;
    setAttributing({ payment_id: pid, partner_id: row.partner_id });
    setBookingSel("");
    try {
      const { data } = await api.get(`/partner-accounts/${row.partner_id}/bookings`);
      setBookings(data || []);
    } catch { setBookings([]); }
  };
  const confirmAttribute = async () => {
    if (!attributing || !bookingSel) { toast.error("Pick a booking"); return; }
    const [bid, kind] = bookingSel.split("|");
    setBusy(true);
    try {
      await api.patch(`/partner-accounts/${attributing.partner_id}/payments/${attributing.payment_id}`, {
        status: "verified", booking_id: bid, booking_kind: kind,
      });
      toast.success("Verified & attributed");
      setAttributing(null); setBookingSel("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!loaded || rows.length === 0) return null;

  // Totals per currency across selected rows — the Founder sees exactly
  // how much they're about to acknowledge before hitting Verify.
  const selectedTotals = {};
  for (const r of rows) {
    if (!sel.has(r.id)) continue;
    const c = (r.currency || "USD").toUpperCase();
    selectedTotals[c] = (selectedTotals[c] || 0) + (Number(r.amount) || 0);
  }

  return (
    <div className="aii-card p-5 border-l-4 border-amber-500" data-testid="pending-partner-payments">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold flex items-center gap-1"><Wallet size={11}/> Pending partner payments</div>
          <h3 className="font-display font-semibold text-base mt-0.5">{rows.length} awaiting your review</h3>
        </div>
        <div className="flex items-center gap-2">
          {Object.entries(selectedTotals).sort(([a],[b])=>a.localeCompare(b)).map(([c, v]) => (
            <span key={c} className="text-xs px-2 py-1 rounded-md bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] font-semibold">
              {SYM[c] || c}{Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          ))}
          <button data-testid="pp-attribute" onClick={openAttribute} disabled={busy || sel.size !== 1}
            className="text-xs px-3 py-1.5 border border-brand text-brand rounded font-semibold hover:bg-[color:var(--aii-green-tint)] disabled:opacity-50 flex items-center gap-1">
            <Link2 size={11}/> Attribute & verify
          </button>
          <button data-testid="pp-verify-all" onClick={() => runBulk("verified")} disabled={busy || sel.size === 0}
            className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1">
            <CheckCircle2 size={11}/> Verify {sel.size || ""}
          </button>
          <button data-testid="pp-reject-all" onClick={() => runBulk("rejected")} disabled={busy || sel.size === 0}
            className="text-xs px-3 py-1.5 border border-red-300 text-red-700 rounded font-semibold hover:bg-red-50 disabled:opacity-50 flex items-center gap-1">
            <XCircle size={11}/> Reject
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="aii-table">
          <thead><tr>
            <th className="w-8"><input type="checkbox" data-testid="pp-sel-all" checked={sel.size === rows.length && rows.length > 0} onChange={toggleAll}/></th>
            <th>Partner</th><th>Date</th><th>Amount</th><th>Method</th><th>Reference</th><th>Note</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`pp-row-${r.id}`} className={sel.has(r.id) ? "bg-[color:var(--aii-green-tint)]/40" : ""}>
                <td><input type="checkbox" data-testid={`pp-sel-${r.id}`} checked={sel.has(r.id)} onChange={() => toggle(r.id)}/></td>
                <td className="font-medium">{r.partner_name || "—"}</td>
                <td className="text-xs">{fmtDate(r.entry_date)}</td>
                <td className="font-semibold text-brand">{SYM[r.currency] || r.currency}{Number(r.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                <td className="uppercase text-xs">{r.method}</td>
                <td className="text-xs max-w-[140px] truncate" title={r.reference}>{r.reference || "—"}</td>
                <td className="text-xs max-w-[220px] truncate" title={r.note}>{r.note || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {attributing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="pp-attribute-modal">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display font-semibold">Attribute payment to a booking</h3>
              <button onClick={() => setAttributing(null)}><XCircle size={16}/></button>
            </div>
            <p className="text-sm text-[color:var(--aii-text-2)] mb-3">
              Snap this deposit onto a specific participant so it shows up in their per-booking balance instead of the unallocated bucket.
            </p>
            <select data-testid="pp-attribute-booking" value={bookingSel} onChange={e => setBookingSel(e.target.value)}
              className="w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm mb-4">
              <option value="">— Pick a booking —</option>
              {bookings.map(b => (
                <option key={`${b.id}-${b.kind}`} value={`${b.id}|${b.kind}`}>
                  {b.kind === "weekend" ? "🗺️" : "✈️"} {b.customer} · {b.programme || "—"} · {b.currency}{Number(b.amount || 0).toLocaleString()}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setAttributing(null)} className="px-4 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50">Cancel</button>
              <button data-testid="pp-attribute-save" onClick={confirmAttribute} disabled={busy || !bookingSel}
                className="px-4 py-2 text-sm bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50">
                Attribute & verify
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
